import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Box,
    Typography,
    Alert,
    Select,
    FormControl,
    InputLabel
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';

/**
 * Props for the ReportProblemModal component
 */
interface ReportProblemModalProps {
    /** Whether the modal is open */
    open: boolean;
    /** Function to call when the modal is closed */
    onClose: () => void;
    /** Default subject for the report */
    defaultSubject?: string;
}

/**
 * Modal component for users to report technical problems.
 *
 * @param props - The props for the component
 * @returns The Report Problem Modal UI
 */
const ReportProblemModal: React.FC<ReportProblemModalProps> = ({ open, onClose, defaultSubject }) => {
    const { user } = useAuth();
    const [type, setType] = useState(defaultSubject ? 'content' : 'bug');
    const [description, setDescription] = useState(defaultSubject ? `${defaultSubject}\n\n` : '');
    const [priority, setPriority] = useState('medium');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const { showToast } = useToast();

    const handleSubmit = async () => {
        if (!type || !description || !priority) {
            showToast('Veuillez remplir tous les champs', 'error');
            return;
        }

        setLoading(true);
        setLoading(true);
        try {
            await api.post('/api/errors/report', {
                type,
                message: description,
                priority,
                pageUrl: window.location.href,
                userInfo: {
                    userAgent: navigator.userAgent,
                    language: navigator.language,
                    screen: `${window.screen.width}x${window.screen.height}`
                }
            });
            showToast('Votre signalement a été envoyé avec succès', 'success');
            handleClose();
        } catch (error) {
            console.error('Error sending report:', error);
            showToast('Erreur lors de l\'envoi du signalement', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setType('bug');
        setDescription('');
        setPriority('medium');
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 700 }}>
                Signaler un problème
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Quicksand, sans-serif' }}>
                        Rencontrez-vous des difficultés ? Décrivez votre problème ci-dessous et notre équipe vous aidera dans les plus brefs délais.
                    </Typography>

                    <FormControl fullWidth>
                        <InputLabel id="type-label" sx={{ fontFamily: 'Quicksand, sans-serif' }}>Type de problème</InputLabel>
                        <Select
                            labelId="type-label"
                            value={type}
                            label="Type de problème"
                            onChange={(e) => setType(e.target.value)}
                            sx={{ fontFamily: 'Quicksand, sans-serif' }}
                        >
                            <MenuItem value="bug">Bug technique</MenuItem>
                            <MenuItem value="content">Problème de contenu</MenuItem>
                            <MenuItem value="access">Problème d'accès</MenuItem>
                            <MenuItem value="other">Autre</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel id="priority-label" sx={{ fontFamily: 'Quicksand, sans-serif' }}>Priorité</InputLabel>
                        <Select
                            labelId="priority-label"
                            value={priority}
                            label="Priorité"
                            onChange={(e) => setPriority(e.target.value)}
                            sx={{ fontFamily: 'Quicksand, sans-serif' }}
                        >
                            <MenuItem value="low">Basse</MenuItem>
                            <MenuItem value="medium">Moyenne</MenuItem>
                            <MenuItem value="high">Haute</MenuItem>
                            <MenuItem value="critical">Critique</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        label="Description détaillée"
                        multiline
                        rows={4}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Veuillez décrire le problème en détail..."
                        fullWidth
                        InputProps={{
                            style: { fontFamily: 'Quicksand, sans-serif' }
                        }}
                        InputLabelProps={{
                            style: { fontFamily: 'Quicksand, sans-serif' }
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    sx={{ fontFamily: 'Quicksand, sans-serif', textTransform: 'none' }}
                >
                    Annuler
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    sx={{ fontFamily: 'Quicksand, sans-serif', textTransform: 'none', fontWeight: 600 }}
                >
                    {loading ? 'Envoi...' : 'Envoyer le signalement'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReportProblemModal;
